import React, { useEffect, useRef, useState } from "react";
import { styled, createTheme, ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import LogoutIcon from "@mui/icons-material/Logout";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import AutorenewOutlinedIcon from "@mui/icons-material/AutorenewOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import CompareArrowsOutlinedIcon from "@mui/icons-material/CompareArrowsOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import {
  Toolbar,
  Typography,
  Box,
  Button,
  List,
  ListItem,
  Drawer as MuiDrawer,
  Container,
  Menu,
  CircularProgress,
  Stack,
  Snackbar,
  Alert,
} from "@mui/material";
import AppsIcon from "@mui/icons-material/Apps";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import RecordVoiceOverOutlinedIcon from "@mui/icons-material/RecordVoiceOverOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import StoreOutlinedIcon from "@mui/icons-material/StoreOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import RuleOutlinedIcon from "@mui/icons-material/RuleOutlined";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import QueryStatsOutlinedIcon from "@mui/icons-material/QueryStatsOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import DnsOutlinedIcon from "@mui/icons-material/DnsOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import {
  allowedSections,
  canSee,
  firstAllowedLanding,
} from "../../utils/sectionPermissions";
import { NavLink, useNavigate, useLocation, Outlet } from "react-router-dom";
import Applogo from "../../img/images/logo.png";
import cookie from "cookiejs";
import { Link } from "react-router-dom";
import Notification from "./Notification";
const drawerWidth = 244;

const REACT_APP_SERVER_URL = process.env.REACT_APP_SERVER_URL;
const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  "& .MuiDrawer-paper": {
    position: "relative",
    whiteSpace: "nowrap",
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    boxSizing: "border-box",
    ...(!open && {
      overflowX: "hidden",
      transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      width: theme.spacing(7),
      [theme.breakpoints.up("sm")]: {
        width: theme.spacing(9),
      },
    }),
  },
}));
const mdTheme = createTheme({
  typography: {
    fontFamily: ["League Spartan", "sans-serif"].join(","),
  },
});

const DashBoard = (props) => {
  const navigate = useNavigate();
  /**
   * COLLAPSIBLE SIDEBAR SECTIONS
   * ============================
   * Six groups all expanded at once made the nav taller than the viewport, so
   * reaching Marketplace — the section people actually work in — meant
   * scrolling past three groups they rarely touch, every single time.
   *
   * Collapse state is per-user and sticky: the group you keep closed stays
   * closed across reloads. The group containing the page you are ON is always
   * opened, so a collapsed sidebar can never hide where you currently are.
   */
  const location = useLocation();
  const navScrollRef = useRef(null);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("adminNavCollapsed") || "{}");
    } catch {
      return {};
    }
  });

  const toggleSection = (title) =>
    setCollapsed((prev) => {
      const next = { ...prev, [title]: !prev[title] };
      try {
        localStorage.setItem("adminNavCollapsed", JSON.stringify(next));
      } catch {
        /* storage disabled — collapsing still works for this session */
      }
      return next;
    });

  /**
   * Keep the sidebar where the user left it.
   *
   * Navigating re-rendered the drawer and the scroll container snapped back to
   * the top, so clicking Product Approvals scrolled you away from Product
   * Approvals. Capture the offset on every scroll and put it back after the
   * route change commits.
   */
  const navScrollTop = useRef(0);
  useEffect(() => {
    const el = navScrollRef.current;
    if (el) el.scrollTop = navScrollTop.current;
  }, [location.pathname]);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = React.useState(null);
  const [statusData, setStatusData] = React.useState("");
  const [role, setRole] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const [showNotificationDialog, setShowNotificationDialog] =
    React.useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  React.useEffect(() => {
    setIsLoading(false);
    let isLogin = cookie.get("token");
    if (!isLogin) {
      // Stash the originally-requested deep-link path so Login can land
      // the admin back here after auth instead of always falling to
      // /dashboard/home. pathname+search+hash so any filters / anchors
      // on a shared link survive.
      try {
        const intended = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        if (intended && intended !== "/login" && intended !== "/") {
          sessionStorage.setItem("postLoginRedirect", intended);
        }
      } catch {
        // sessionStorage can throw in private-mode iframes; ignore.
      }
      navigate("/login");
    }
    fetch(`${REACT_APP_SERVER_URL}/get_profile`, {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      credentials: "include",
      method: "GET",
    })
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          console.log("some error");
          setIsLoading(true);
        }
      })
      .then((data) => {
        // First .then returns undefined when response status != 200; guard
        // before reading .data to avoid the uncaught TypeError that's been
        // killing the dashboard on auth/network hiccups.
        if (data && data.data) {
          setRole(data.data.role);
          // Lift the role to App state so peer routes (MyApps, etc.)
          // can render super-admin-only controls without re-fetching.
          if (typeof props.setAppRole === "function") {
            props.setAppRole(data.data.role);
          }
          if (data.data.role !== "super_admin" && data.data.role !== "admin") {
            props.setUserRole(data.data.option);
            // Member landed on a section they're not allowed into?
            // Bounce them to their first allowed landing rather than
            // showing the "No Access" splash for every blocked URL.
            const activeKey = window.location.pathname.split("/")[2];
            if (activeKey && !canSee(data.data.option, activeKey)) {
              navigate(firstAllowedLanding(data.data.option), {
                replace: true,
              });
            }
          }
          setStatusData(data.data.status);
          setIsLoading(true);
        }
      })
      .catch((error) => {
        setIsLoading(true);
        console.log(error);
      });
    // eslint-disable-next-line
  }, []);

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleProfileMenuClose = () => {
    setProfileMenuOpen(null);
  };
  const handleProfileMenuOpen = (event) => {
    setProfileMenuOpen(event.currentTarget);
  };
  const handleLogoutAPI = (e) => {
    e.preventDefault();
    fetch(`${REACT_APP_SERVER_URL}/logout`, {
      headers: {
        Accept: "application/json",
      },
      credentials: "include",
      method: "GET",
    }).then((response) => {
      if (response.status === 200) {
        localStorage.clear();
        console.log("User Logged out");
        navigate("/login");
        return response.json();
      } else {
        console.log("Some error");
        localStorage.clear();
      }
    });
  };

  return (
    <>
      {snackbarOpen && (
        <div
          style={{
            height: "30px",
            fontSize: "16px",
            display: "flex",
            gap: "10px",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "#D6EBFB",
          }}
        >
          <p>
            {" "}
            Please activate your account by verifying your email address to
            purchase products
          </p>

          <Link
            style={{
              backgroundColor: "#7953FF",
              paddingLeft: "10px",
              paddingRight: "10px",
              color: "#fff",
              borderRadius: "5px",
              textDecoration: "none",
            }}
            to="/verifyemail"
            target="blank"
          >
            activate
          </Link>

          <Button
            sx={{
              backgroundColor: "#ff3333",
              borderRadius: "5px",
              fontSize: "10px",
              height: "23px",
              px: 2,
              color: "#fff",
              fontWeight: 600,
              border: "1px Solid #ff3333",
              ":hover": { color: "#ff3333" },
            }}
            onClick={handleSnackbarClose}
          >
            close
          </Button>
        </div>
      )}
      {!isLoading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            mt: 55,
          }}
        >
          <CircularProgress />
        </Box>
      )}
      {isLoading && (
        <>
          {statusData === "active" && (
            <>
              <ThemeProvider theme={mdTheme}>
                <Box
                  sx={{ display: "flex", height: "100vh", overflow: "hidden" }}
                >
                  <CssBaseline />
                  <Drawer
                    variant="permanent"
                    open={true}
                    sx={{
                      overflowX: "hidden",
                      background: "red",
                      height: "100vh",
                    }}
                  >
                    <Toolbar
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        px: [1],
                        width: "96%",
                        margin: "2%",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "22px",
                          fontWeight: "600",
                          lineHeight: "28px",
                        }}
                      >
                        <img
                          src={Applogo}
                          alt=""
                          style={{
                            width: "112%",
                            marginLeft: -18,
                            padding: 26,
                            cursor: "pointer",
                          }}
                          onClick={() => navigate("/dashboard/myapps")}
                        />
                      </Typography>
                    </Toolbar>
                    <div
                      ref={navScrollRef}
                      onScroll={(e) => {
                        navScrollTop.current = e.currentTarget.scrollTop;
                      }}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        height: "100%",
                        overflowY: "auto",
                      }}
                    >
                      <List
                        sx={{
                          pl: 2.5,
                          fontFamily: `'Inter', sans-serif`,
                          height: "auto",
                        }}
                      >
                        {(() => {
                          const activeKey = window.location.pathname.split("/")[2];

                          const itemSx = (key) => ({
                            backgroundColor: activeKey === key ? "#F7F7F9" : "none",
                            borderRadius: "50px 0px 0px 50px",
                            borderRight:
                              activeKey === key ? "4px solid #7953FF" : "none",
                            width: "100%",
                            fontWeight: activeKey === key ? "1000" : "",
                            mb: 1.2,
                          });

                          const Item = ({ to, icon: Icon, label }) => (
                            <NavLink
                              to={to}
                              key={to}
                              style={{
                                textDecoration: "none",
                                color: "#4F4F4F",
                                width: "100%",
                              }}
                            >
                              <ListItem sx={itemSx(to)}>
                                <Icon style={{ marginRight: 10 }} />
                                {label}
                              </ListItem>
                            </NavLink>
                          );

                          // A heading you can actually click. The chevron
                          // points down when open, right when closed.
                          const SectionLabel = ({ children, open, onClick, locked }) => (
                            <Typography
                              component="button"
                              type="button"
                              onClick={locked ? undefined : onClick}
                              title={
                                locked
                                  ? "You are inside this section"
                                  : open
                                    ? "Collapse"
                                    : "Expand"
                              }
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                width: "calc(100% - 8px)",
                                background: "none",
                                border: "none",
                                textAlign: "left",
                                cursor: locked ? "default" : "pointer",
                                fontSize: 10.5,
                                fontWeight: 800,
                                letterSpacing: "0.12em",
                                textTransform: "uppercase",
                                color: locked ? "#1372b3" : "#8696aa",
                                mt: 2,
                                mb: 0.8,
                                pl: 1,
                                py: 0.4,
                                borderRadius: "6px",
                                "&:hover": { background: locked ? "none" : "#f1f5f9" },
                              }}
                            >
                              <ExpandMoreIcon
                                sx={{
                                  fontSize: 15,
                                  transition: "transform .15s",
                                  transform: open ? "none" : "rotate(-90deg)",
                                  opacity: locked ? 0.5 : 1,
                                }}
                              />
                              {children}
                            </Typography>
                          );

                          const sections = [
                            {
                              title: "User Management",
                              items: [
                                { to: "myapps", icon: AppsIcon, label: "All Workspace" },
                                { to: "allusers", icon: PeopleAltOutlinedIcon, label: "All Users" },
                              ],
                            },
                            {
                              // Not in sectionPermissions SECTIONS on purpose:
                              // every portal user (members too) gets self-
                              // service here; what they see inside follows
                              // their HRMS role.
                              title: "HRMS",
                              items: [
                                { to: "hrms", icon: BadgeOutlinedIcon, label: "HRMS" },
                              ],
                            },
                            {
                              title: "Growth Tools",
                              items: [
                                { to: "renewal_reminders", icon: AutorenewOutlinedIcon, label: "Renewal Reminders" },
                                { to: "warm_leads", icon: EmailOutlinedIcon, label: "Warm Leads" },
                                { to: "inbound_funnel", icon: InsightsOutlinedIcon, label: "Inbound Funnel" },
                                { to: "revenue", icon: PaidOutlinedIcon, label: "Revenue Tracker" },
                                { to: "compare_pages", icon: CompareArrowsOutlinedIcon, label: "Compare Pages" },
                              ],
                            },
                            {
                              title: "Inbound Contacts",
                              items: [
                                { to: "email_contact", icon: MailOutlineIcon, label: "Email Contact Us" },
                                { to: "contact", icon: SupportAgentOutlinedIcon, label: "Contact Us" },
                                { to: "adcontact", icon: RecordVoiceOverOutlinedIcon, label: "Ad Contact Us" },
                                { to: "ref", icon: LinkOutlinedIcon, label: "Ref Count" },
                                { to: "source", icon: ShareOutlinedIcon, label: "Source Count" },
                              ],
                            },
                            {
                              // One Marketplace group, with the two things an
                              // ops person actually works — reviews and the
                              // vendors behind them — at the top. Catalog
                              // screens follow, unchanged.
                              title: "Marketplace",
                              items: [
                                // ONE inbox. Catalogue requests (a vendor
                                // asking for a plan, a period or a rename) are
                                // listed inside Product Approvals with their
                                // own change type and open their own review
                                // screen — a second queue is a queue nobody
                                // remembers to visit. Duplicate Shelves is a
                                // cleanup tool, not a daily screen, so it is
                                // reached from Set Default, where the problem
                                // it fixes is visible. Both routes still exist
                                // for deep links.
                                { to: "productapproval", icon: RuleOutlinedIcon, label: "Product Approvals" },
                                { to: "vendors", icon: StorefrontOutlinedIcon, label: "Vendors" },
                                { to: "marketplace", icon: StoreOutlinedIcon, label: "Catalog" },
                                { to: "adminproducts", icon: Inventory2OutlinedIcon, label: "Admin Products" },
                                { to: "order_approval", icon: AssignmentOutlinedIcon, label: "Order Approvals" },
                                { to: "ingram_failed_orders", icon: AssignmentOutlinedIcon, label: "Failed Ingram Orders" },
                                { to: "ingram_mapping", icon: AssignmentOutlinedIcon, label: "Ingram Mapping" },
                                { to: "website_apps", icon: LanguageOutlinedIcon, label: "Website Apps" },
                                { to: "taxonomy", icon: LocalOfferOutlinedIcon, label: "Taxonomy" },
                              ],
                            },
                            {
                              title: "Email Marketing",
                              items: [
                                { to: "newsletters", icon: CampaignOutlinedIcon, label: "Newsletter" },
                                { to: "monthly-digests", icon: MarkEmailReadOutlinedIcon, label: "Monthly Digest" },
                                { to: "email-analytics", icon: QueryStatsOutlinedIcon, label: "Email Analytics" },
                              ],
                            },
                            {
                              title: "Content & Publishing",
                              items: [
                                { to: "blog-studio", icon: ArticleOutlinedIcon, label: "Blog Studio" },
                              ],
                            },
                            {
                              title: "Finance",
                              items: [
                                { to: "zoho_billing", icon: ReceiptLongOutlinedIcon, label: "Zoho Billing" },
                              ],
                            },
                            {
                              title: "Domain Extractor",
                              items: [
                                { to: "domain_extractor", icon: DnsOutlinedIcon, label: "Domain Extractor" },
                              ],
                            },
                          ];

                          // Filter sections / items by member permissions
                          // (admin / super_admin pass through unchanged).
                          const visibleSections = sections
                            .map((sec) => ({
                              ...sec,
                              items: sec.items.filter((it) =>
                                canSee(props.userRole, it.to),
                              ),
                            }))
                            .filter((sec) => sec.items.length > 0);
                          return (
                            <>
                              {role === "super_admin" && (
                                <Item to="invite" icon={GroupOutlinedIcon} label="Invite user" />
                              )}
                              {/* Dashboard (home) removed — route also commented in App.js */}
                              {visibleSections.map((sec) => {
                                // The section holding the current page is
                                // always open and cannot be collapsed — a
                                // sidebar must never hide where you are.
                                const here = sec.items.some(
                                  (it) =>
                                    location.pathname.split("/")[2] === it.to,
                                );
                                const open = here || !collapsed[sec.title];
                                return (
                                  <React.Fragment key={sec.title}>
                                    <SectionLabel
                                      open={open}
                                      locked={here}
                                      onClick={() => toggleSection(sec.title)}
                                    >
                                      {sec.title}
                                    </SectionLabel>
                                    {open &&
                                      sec.items.map((it) => (
                                        <Item key={it.to} {...it} />
                                      ))}
                                  </React.Fragment>
                                );
                              })}
                              {/* Mail Box removed — route also commented in App.js */}
                            </>
                          );
                        })()}
                      </List>
                    </div>
                  </Drawer>
                  <Box
                    component="main"
                    sx={{
                      flexGrow: 1,
                      height: "100vh",
                      overflow: "auto",
                    }}
                  >
                    <Container sx={{ mt: 0, maxWidth: "1300px!important" }}>
                      <Box
                        sx={{
                          display: "flex",
                          width: "100%!importnat",
                          justifyContent: "space-between",
                          mt: 2,
                        }}
                      >
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          spacing={2}
                          flexWrap="wrap"
                          sx={{ width: "55%", pt: 2 }}
                        >
                          {window.location.pathname.split("/")[2] ===
                            "home" && (
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "end",
                              }}
                            >
                              <Typography
                                color="#000"
                                sx={{
                                  float: "left",
                                  fontSize: "24px",
                                  lineHeight: "36px",
                                  fontWeight: "700",
                                  fontStyle: "normal",
                                }}
                              >
                                DashBoard
                              </Typography>
                              <Typography>
                                Get details of your recent activities. Your
                                recenet payments, subscriptions and much more.
                              </Typography>
                            </Box>
                          )}
                          {window.location.pathname.split("/")[2] ===
                            "myapps" && (
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "end",
                              }}
                            >
                              <Typography
                                color="#000"
                                sx={{
                                  float: "left",
                                  fontSize: "24px",
                                  lineHeight: "36px",
                                  fontWeight: "700",
                                  fontStyle: "normal",
                                }}
                              >
                                All Workspace
                              </Typography>
                              <Typography>
                                See and manage all the workspace & users
                              </Typography>
                            </Box>
                          )}
                          {window.location.pathname.split("/")[2] ===
                            "allusers" && (
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "end",
                              }}
                            >
                              <Typography
                                color="#000"
                                sx={{
                                  float: "left",
                                  fontSize: "24px",
                                  lineHeight: "36px",
                                  fontWeight: "700",
                                  fontStyle: "normal",
                                }}
                              >
                                All Users
                              </Typography>
                              <Typography>
                                See and manage all the users
                              </Typography>
                            </Box>
                          )}
                        </Stack>
                        <Box
                          sx={{
                            display: "flex",
                            width: "45%",
                            flexDirection: "row",
                            alignItems: "center",
                          }}
                        >
                          <Box sx={{ width: "100%!important" }}>
                            <Box
                              sx={{
                                float: "right",
                                display: "flex",
                                height: "6vh",
                                justifyContent: "end",
                                pr: "-2",
                              }}
                            >
                              <Box sx={{ pt: "0.1em" }}>
                                <Button
                                  onClick={() =>
                                    setShowNotificationDialog(
                                      !showNotificationDialog,
                                    )
                                  }
                                >
                                  Push Notification
                                </Button>
                                <Button
                                  variant="outlined"
                                  onClick={handleProfileMenuOpen}
                                  sx={{ mx: 2, height: "5vh", mr: 0 }}
                                >
                                  Lakha
                                </Button>
                                <Menu
                                  id="menu-appbar"
                                  anchorEl={profileMenuOpen}
                                  anchorOrigin={{
                                    vertical: "bottom",
                                    horizontal: "right",
                                  }}
                                  keepMounted
                                  transformOrigin={{
                                    vertical: "top",
                                    horizontal: "right",
                                  }}
                                  open={Boolean(profileMenuOpen)}
                                  onClose={handleProfileMenuClose}
                                  sx={{ width: "50%", mt: 1 }}
                                >
                                  <Button
                                    sx={{ mx: 2, height: "25px" }}
                                    onClick={handleLogoutAPI}
                                    endIcon={<LogoutIcon />}
                                  >
                                    {" "}
                                    Logout{" "}
                                  </Button>
                                </Menu>
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                        {showNotificationDialog && (
                          <Notification
                            onClose={setShowNotificationDialog}
                            showSnackbar={showSnackbar}
                          />
                        )}
                      </Box>
                      {/* all the sections which comes below nav header */}
                      {/* HRMS draws its own header band, so it sits tight under the nav. */}
                      <Box
                        sx={
                          location.pathname.includes("/hrms")
                            ? { pt: 0.5, mb: 3, mt: 0 }
                            : { py: 2, mb: 5, mt: 3 }
                        }
                      >
                        <Outlet />
                        {/* <Routes>
                                <Route path='campaign' element={<Campaign />} />
                                <Route path='dashboard' element={<Dashboard />} />
                                <Route path='email-accounts' element={<EmailAccounts />} />
                                <Route path='integrations' element={<Integrations />} />
                                <Route path='common-mailbox' element={<CommonMailbox/>}/>
                            </Routes> */}
                      </Box>
                    </Container>
                  </Box>
                </Box>
              </ThemeProvider>
            </>
          )}
          {statusData !== "active" && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100vh",
                width: "100%",
                flexDirection: "column",
              }}
            >
              <h2
                style={{
                  textAlign: "center",
                  color: "#495057",
                  fontSize: "1.5rem",
                  fontWeight: "500",
                  fontFamily: "Arial, sans-serif",
                }}
              >
                Your account is currently disabled. Please contact your
                workspace owner to enable it.
              </h2>
              <button
                sx={{ mx: 2, height: "25px", width: "30px", p: 2 }}
                onClick={handleLogoutAPI}
              >
                Logout <LogoutIcon />
              </button>
            </Box>
          )}
        </>
      )}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default DashBoard;
